// ⚠ AUTO-GENERATED from components/cs/cs.js — DO NOT EDIT MANUALLY
// 수정은 마스터(components/cs/cs.js)에서 하고 'npm run cs:sync' 실행

// components.jsx — choishin 7 core components
// Card · Button · Input · Nav · Modal · ChartContainer · MetricCard
// + small primitives: Badge, Tag, Avatar, IconBtn, Tabs, Toggle

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

/* ─────────── Card ─────────── */
function CSCard({ children, style, padded = true, tone = "surface", onClick, className = "", ...rest }) {
  const bg = tone === "sunken" ? "var(--cs-surface-2)" : tone === "ink" ? "var(--cs-accent)" : "var(--cs-surface)";
  const color = tone === "ink" ? "var(--cs-accent-ink)" : "var(--cs-ink)";
  return (
    <div
      className={"cs-card " + className}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(e); } } : undefined}
      style={{
        background: bg, color,
        borderRadius: "var(--cs-r-lg)",
        border: "none",
        padding: padded ? "var(--cs-s-cardpad)" : 0,
        boxShadow: "var(--cs-shadow-1)",
        cursor: onClick ? "pointer" : "default",
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

/* ─────────── Button ─────────── */
// 터치 기기는 탭하면 mouseenter만 오고 leave가 안 와서 hover 색이 눌어붙는다 → 진짜 hover 가능한 포인터에서만 켠다
const canHover = () => typeof window !== "undefined" && window.matchMedia("(hover: hover)").matches;

function CSBtn({ children, variant = "primary", size = "md", icon, iconRight, onClick, disabled, style, full, className = "", ...rest }) {
  const [hover, setHover] = useState(false);
  const variants = {
    primary:   { bg: hover ? "#0F231B" : "var(--cs-accent)", color: "var(--cs-accent-ink)", border: "transparent" },
    // secondary만 유일하게 테두리를 쓰던 것을 제거(2026-08-22 design.md §G-2) — 경계는 배경 명도 대비로.
    // 배경을 surface-2로 한 단 내려 카드(surface) 위에서 버튼이 "파이게" 만든다. hover는 한 단 더 진한 line.
    secondary: { bg: hover ? "var(--cs-line)" : "var(--cs-surface-2)", color: "var(--cs-ink)", border: "transparent" },
    ghost:     { bg: hover ? "rgba(0,0,0,.04)" : "transparent", color: "var(--cs-ink)", border: "transparent" },
    danger:    { bg: hover ? "#B82E27" : "var(--cs-rise)", color: "#fff", border: "transparent" },
    gold:      { bg: hover ? "#9B701F" : "var(--cs-gold)", color: "#fff", border: "transparent" },
  };
  const sizes = {
    sm: { h: 32, px: 12, fs: 13, gap: 6 },
    md: { h: 40, px: 16, fs: 14, gap: 8 },
    lg: { h: 52, px: 20, fs: 15, gap: 10 },
  };
  const v = variants[variant], s = sizes[size];
  return (
    <button
      // cs-btn = :active 눌림 피드백(scale var(--cs-press))을 cs-tokens.css에서 건다 — 인라인 style로는 :active 불가.
      // 호출부 className은 덮지 않고 뒤에 합친다.
      className={("cs-btn " + className).trim()}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => { if (canHover()) setHover(true); }} onMouseLeave={() => setHover(false)}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: s.gap,
        // #9 Optical align — 왼쪽 아이콘만 있을 때 좌측 패딩 3px 축소(아이콘이 떠 보이는 착시 보정 → 시각 중앙)
        height: s.h, padding: (icon && !iconRight) ? `0 ${s.px}px 0 ${s.px - 3}px` : `0 ${s.px}px`, fontSize: s.fs, fontWeight: 600,
        background: v.bg, color: v.color,
        border: `1px solid ${v.border}`, borderRadius: "var(--cs-r-pill)",
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
        // §G-5 토큰 — 바뀌는 속성(배경·눌림)만. box-shadow는 CSBtn이 쓰지 않아 제거
        transition: "background var(--cs-dur-fast) var(--cs-ease-out), transform var(--cs-dur-fast) var(--cs-ease-out)",
        letterSpacing: "-0.01em", whiteSpace: "nowrap",
        width: full ? "100%" : "auto",
        ...style,
      }}
      {...rest}
    >
      {icon}<span>{children}</span>{iconRight}
    </button>
  );
}

/* ─────────── Input ─────────── */
function CSInput({ label, value, onChange, placeholder, prefix, suffix, type = "text", hint, error, size = "md", style, ...rest }) {
  const [focus, setFocus] = useState(false);
  const h = size === "sm" ? 38 : size === "lg" ? 54 : 46;
  return (
    <label style={{ display: "block", ...style }}>
      {label && (
        <div style={{ fontSize: 12, fontWeight: 500, color: "var(--cs-mute)", marginBottom: 6, letterSpacing: "-0.005em" }}>
          {label}
        </div>
      )}
      <div style={{
        display: "flex", alignItems: "center", height: h,
        background: "var(--cs-surface)",
        border: `1px solid ${error ? "var(--cs-rise)" : focus ? "var(--cs-ink)" : "var(--cs-line-2)"}`,
        borderRadius: "var(--cs-r-md)", padding: "0 14px", gap: 8,
        transition: "border-color .15s, box-shadow .15s",
        boxShadow: focus ? "0 0 0 3px rgba(26,23,20,.08)" : "none",
      }}>
        {prefix && <span style={{ color: "var(--cs-mute)", fontSize: 14 }}>{prefix}</span>}
        <input
          type={type} value={value} onChange={onChange} placeholder={placeholder}
          onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
          style={{
            flex: 1, border: 0, outline: 0, background: "transparent",
            font: "inherit", fontSize: size === "lg" ? 16 : 15, color: "var(--cs-ink)",
            fontFamily: type === "number" ? "var(--cs-font-mono)" : "inherit",
            minWidth: 0,
          }}
          {...rest}
        />
        {suffix && <span style={{ color: "var(--cs-mute)", fontSize: 14, fontFamily: "var(--cs-font-mono)" }}>{suffix}</span>}
      </div>
      {hint && !error && <div style={{ fontSize: 12, color: "var(--cs-mute)", marginTop: 6 }}>{hint}</div>}
      {error && <div style={{ fontSize: 12, color: "var(--cs-rise)", marginTop: 6 }}>{error}</div>}
    </label>
  );
}

/* ─────────── Tabs ─────────── */
function CSTabs({ items, value, onChange, fullWidth, style }) {
  return (
    <div style={{
      display: "inline-flex", padding: 4, background: "var(--cs-surface-2)",
      borderRadius: "var(--cs-r-pill)", gap: 2,
      width: fullWidth ? "100%" : "auto",
      ...style,
    }}>
      {items.map((it) => {
        const active = it.value === value;
        return (
          <button key={it.value} onClick={() => onChange(it.value)} style={{
            flex: fullWidth ? 1 : "0 0 auto",
            padding: "8px 14px", fontSize: 13, fontWeight: 600,
            background: active ? "var(--cs-surface)" : "transparent",
            color: active ? "var(--cs-ink)" : "var(--cs-mute)",
            border: 0, borderRadius: "var(--cs-r-pill)",
            cursor: "pointer", transition: "background .15s, color .15s",
            boxShadow: active ? "var(--cs-shadow-1)" : "none",
          }}>
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

/* ─────────── Bottom Nav (mobile) ─────────── */
function CSBottomNav({ items, value, onChange }) {
  return (
    <nav style={{
      position: "absolute", left: 0, right: 0, bottom: 0,
      display: "grid", gridTemplateColumns: `repeat(${items.length}, 1fr)`,
      background: "var(--cs-overlay)",
      backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
      borderTop: "1px solid var(--cs-line)",
      padding: "8px 4px 22px",
      zIndex: 5,
    }}>
      {items.map(it => {
        const active = it.value === value;
        return (
          <button key={it.value} onClick={() => onChange?.(it.value)} style={{
            display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
            border: 0, background: "transparent", padding: "6px 0", cursor: "pointer",
            color: active ? "var(--cs-ink)" : "var(--cs-mute)",
          }}>
            <CSIcon name={it.icon} size={22} stroke={active ? 2 : 1.5} />
            <span style={{ fontSize: 10.5, fontWeight: active ? 700 : 500, letterSpacing: "-0.01em" }}>{it.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

/* ─────────── Top App Bar ─────────── */
// ⚠ 2026-07-26 타이틀 중앙정렬 수정 — flex(0 auto / 1 / 0 auto)였다.
//   그 구조에선 left(뒤로가기 44px)만 있고 right가 비면 가운데 칸이 좌측 버튼 폭만큼 밀려
//   **타이틀이 오른쪽으로 치우쳤다**(거시 이벤트방 등 — CSAppBar 사용 41개 화면 전부 해당).
//   좌우 칸을 같은 1fr로 잡으면 내용 폭과 무관하게 가운데 칸이 화면 정중앙에 온다.
//   minmax(44px, 1fr): 탭 타깃 최소폭(44)은 보장하면서 균등 분배. 타이틀은 넘치면 말줄임.
function CSAppBar({ title, left, right, sub, sticky }) {
  return (
    <header style={{
      position: sticky ? "sticky" : "relative", top: 0, zIndex: 4,
      background: "var(--cs-bg)",
      padding: "12px 16px 10px",
      display: "grid", gridTemplateColumns: "minmax(44px, 1fr) auto minmax(44px, 1fr)",
      alignItems: "center", gap: 8, minHeight: 52,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, justifySelf: "start", minWidth: 0 }}>{left}</div>
      <div style={{ minWidth: 0, textAlign: "center" }}>
        <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: "-0.02em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
        {sub && <div style={{ fontSize: 11, color: "var(--cs-mute)", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</div>}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 4, justifySelf: "end", minWidth: 0 }}>{right}</div>
    </header>
  );
}

/* ─────────── Modal / Sheet ─────────── */
function CSModal({ open, onClose, title, children, footer, sheet }) {
  // 마운트 가드 — Next.js SSR 시 document 없음 → createPortal은 클라 마운트 후에만 (2026-06-13 P1 복원: 웹 SSR 안전)
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  // 모달 열림 동안 배경(body) 스크롤 잠금 (2026-06-10 fix) — iOS scroll chaining:
  // 시트 위 스와이프가 뒤 페이지(차트)로 새서 본문 스크롤이 안 되는 것처럼 보이던 문제.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);
  if (!open || !mounted) return null;
  // Portal로 body 직속 렌더 (2026-06-10 fix): 화면 컴포넌트 안 렌더 시 상위 stacking context에 갇혀
  // zIndex를 올려도 BottomNav(z-index 100)가 시트를 덮음. fixed = viewport 기준
  // (absolute+inset:0은 스크롤 긴 부모의 "문서 맨 아래"에 시트가 붙어 iOS 키보드 시 화면 밖으로 밀림).
  // ⚠ body 직속 = .cs-root[data-temp] 변수 스코프 밖 → --cs-accent·warm 톤이 풀려 UI 깨짐
  //   (선택 칩 테두리 소실·버튼 하얘짐) → 래퍼에 cs-root + 현재 화면 data-temp를 복제해 입힘.
  const hostTemp = (typeof document !== "undefined" && document.querySelector('.cs-root[data-temp]')?.getAttribute('data-temp')) || 'warm';
  return createPortal(
    <div onClick={onClose} className="cs-root cs-modal-overlay" data-temp={hostTemp} style={{
      position: "fixed", inset: 0, background: "rgba(26,23,20,0.45)",
      display: "flex", alignItems: sheet ? "flex-end" : "center", justifyContent: "center",
      zIndex: 200, animation: "csFade .18s ease-out", // BottomNav(z-index 100)보다 위 — fixed 전환 후 시트 하단(매수 버튼)이 탭바에 가려지던 문제 (2026-06-10)
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: sheet ? "100%" : "calc(100% - 32px)", maxWidth: sheet ? "none" : 420,
        background: "var(--cs-surface)",
        borderRadius: sheet ? "20px 20px 0 0" : "var(--cs-r-xl)",
        boxShadow: "var(--cs-shadow-pop)",
        overflow: "hidden",
        // 콘텐츠가 화면보다 길면(긴 주문폼 등) 본문만 스크롤 — maxHeight + flex column (2026-06-10 fix)
        maxHeight: sheet ? "calc(100% - 36px)" : "calc(100% - 64px)",
        display: "flex", flexDirection: "column",
        animation: sheet ? "csSlideUp .25s cubic-bezier(.2,.8,.3,1)" : "csPop .22s cubic-bezier(.2,.8,.3,1)",
      }}>
        {sheet && <div style={{ flex: "0 0 auto", width: 40, height: 4, background: "var(--cs-line-2)", borderRadius: 2, margin: "10px auto 0" }} />}
        {title && (
          <div style={{ flex: "0 0 auto", padding: "18px 20px 12px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em" }}>{title}</h3>
            <button onClick={onClose} style={{ border: 0, background: "transparent", cursor: "pointer", color: "var(--cs-mute)", fontSize: 22, lineHeight: 1 }}>×</button>
          </div>
        )}
        {/* 본문에 직접 maxHeight — WebKit이 부모 maxHeight+flex 조합으로 본문 스크롤 높이를 못 잡는 케이스 우회.
            100dvh 기준에서 핸들+헤더+footer 여유 ~170px 제외. 콘텐츠가 짧으면 영향 없음(auto) */}
        <div style={{ flex: "1 1 auto", padding: title ? "0 20px 20px" : 20, overflowY: "auto", minHeight: 0, maxHeight: "calc(100dvh - 170px)", WebkitOverflowScrolling: "touch", overscrollBehavior: "contain", touchAction: "pan-y" }}>{children}</div>
        {footer && <div style={{ flex: "0 0 auto", padding: "12px 20px", borderTop: "1px solid var(--cs-line)", background: "var(--cs-surface-2)", display: "flex", gap: 8, justifyContent: "flex-end" }}>{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/* ─────────── Chart Container ─────────── */
function CSChartContainer({ title, subtitle, range, onRange, legend, action, height = 200, children, dense }) {
  return (
    <CSCard padded>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 4 }}>
        <div>
          <div style={{ fontSize: 13, color: "var(--cs-mute)", fontWeight: 500, letterSpacing: "-0.005em" }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11, color: "var(--cs-mute)", marginTop: 2, fontFamily: "var(--cs-font-mono)" }}>{subtitle}</div>}
        </div>
        {action}
      </div>
      {legend && (
        <div style={{ display: "flex", gap: 14, marginTop: 6, marginBottom: dense ? 4 : 8, flexWrap: "wrap" }}>
          {legend.map((l, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--cs-ink-2)" }}>
              <span style={{ width: 8, height: 8, borderRadius: l.shape === "square" ? 2 : 999, background: l.color, display: "inline-block" }} />
              {l.label}
              {l.value && <span className="num" style={{ color: "var(--cs-mute)", marginLeft: 2 }}>{l.value}</span>}
            </div>
          ))}
        </div>
      )}
      <div style={{ height, position: "relative", marginTop: dense ? 4 : 10 }}>{children}</div>
      {range && (
        <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
          <CSTabs items={range.items} value={range.value} onChange={onRange} />
        </div>
      )}
    </CSCard>
  );
}

/* ─────────── Metric Card ─────────── */
function CSMetric({ label, value, unit, delta, deltaLabel, trend, sparkline, icon, size = "md", style }) {
  const big = size === "lg";
  const trendColor = trend === "up" ? "var(--cs-rise)" : trend === "down" ? "var(--cs-fall)" : "var(--cs-flat)";
  return (
    <CSCard padded style={style}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 12.5, color: "var(--cs-mute)", fontWeight: 500, letterSpacing: "-0.005em" }}>{label}</div>
        {icon && <span style={{ color: "var(--cs-mute-2)" }}>{icon}</span>}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginTop: big ? 10 : 6 }}>
        <span className="num" style={{ fontSize: big ? "var(--cs-t-data-lg)" : "var(--cs-t-data-md)", fontWeight: 600, letterSpacing: "-0.04em", lineHeight: 1.05 }}>
          {value}
        </span>
        {unit && <span style={{ fontSize: 13, color: "var(--cs-mute)", fontFamily: "var(--cs-font-mono)" }}>{unit}</span>}
      </div>
      {(delta != null || sparkline) && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8 }}>
          {delta != null && (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: trendColor, fontFamily: "var(--cs-font-mono)", fontWeight: 600 }}>
              <span>{trend === "up" ? "▲" : trend === "down" ? "▼" : "—"}</span>
              <span>{delta}</span>
              {deltaLabel && <span style={{ color: "var(--cs-mute)", marginLeft: 4, fontFamily: "var(--cs-font-sans)", fontWeight: 500 }}>{deltaLabel}</span>}
            </div>
          )}
          {sparkline}
        </div>
      )}
    </CSCard>
  );
}

/* ─────────── Avatar / Badge / Tag / IconBtn ─────────── */
function CSAvatar({ name, src, size = 36, tone = "neutral", emoji }) {
  const bg = tone === "gold" ? "var(--cs-gold)" : tone === "alert" ? "var(--cs-alert)" : tone === "ink" ? "var(--cs-accent)" : "var(--cs-surface-2)";
  const color = tone === "neutral" ? "var(--cs-ink)" : "#fff";
  return (
    <div style={{
      width: size, height: size, borderRadius: 999, background: bg, color,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      fontWeight: 700, fontSize: size * 0.42, flexShrink: 0,
      border: tone === "neutral" ? "1px solid var(--cs-line)" : "none",
      overflow: "hidden",
    }}>
      {emoji ? <span style={{ fontSize: size * 0.55 }}>{emoji}</span> : (src ? <img src={src} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : name?.slice(0, 1))}
    </div>
  );
}
function CSBadge({ children, tone = "neutral", style }) {
  const tones = {
    neutral: { bg: "var(--cs-surface-2)", color: "var(--cs-ink-2)" },
    rise:    { bg: "var(--cs-rise-soft)", color: "var(--cs-rise)" },
    fall:    { bg: "var(--cs-fall-soft)", color: "var(--cs-fall)" },
    gold:    { bg: "var(--cs-gold-soft)", color: "var(--cs-bull)" },
    alert:   { bg: "var(--cs-alert-soft)", color: "var(--cs-alert)" },
    ink:     { bg: "var(--cs-accent)",    color: "var(--cs-accent-ink)" },
  };
  const t = tones[tone];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 8px", fontSize: 11, fontWeight: 600,
      background: t.bg, color: t.color,
      borderRadius: "var(--cs-r-pill)", letterSpacing: "-0.005em",
      ...style,
    }}>{children}</span>
  );
}
// tone: "ink"(기존 검정 토글) · "fill"(잉크그린 채움 토글 — 지표·이유·감정 칩 수렴, 2026-06-24)
function CSTag({ children, active, onClick, tone = "ink" }) {
  const fill = tone === "fill";
  // active/비활성 × ink/fill 4조합. 비활성 보더는 ink=line-2(강), fill=line(약)
  const bg = active ? (fill ? "var(--cs-accent)" : "var(--cs-ink)") : "transparent";
  const fg = active ? (fill ? "var(--cs-accent-ink)" : "var(--cs-bg)") : (fill ? "var(--cs-mute)" : "var(--cs-ink-2)");
  const bd = active ? (fill ? "var(--cs-accent)" : "var(--cs-ink)") : (fill ? "var(--cs-line)" : "var(--cs-line-2)");
  return (
    <button type="button" onClick={onClick} style={{
      padding: "6px 12px", fontSize: 12.5, fontWeight: fill ? 600 : 500,
      background: bg, color: fg, border: `1px solid ${bd}`,
      borderRadius: "var(--cs-r-pill)", cursor: "pointer",
      // all 금지 — 태그 토글에서 바뀌는 건 배경·글자·테두리색 (2026-09-11 §motion)
      transition: "background var(--cs-dur-fast) ease, color var(--cs-dur-fast) ease, border-color var(--cs-dur-fast) ease",
    }}>{children}</button>
  );
}
function CSIconBtn({ children, onClick, label }) {
  return (
    <button onClick={onClick} aria-label={label} style={{
      width: 44, height: 44, borderRadius: 999, border: 0, cursor: "pointer", // iOS HIG 최소 터치타겟 (2026-06-13 P1 복원)
      background: "transparent", color: "var(--cs-ink)",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      transition: "background .15s",
    }}
    onMouseEnter={e => e.currentTarget.style.background = "rgba(0,0,0,.05)"}
    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
    >{children}</button>
  );
}

/* ─────────── Tiny icons (line, 24px viewbox) ─────────── */
// 아이콘 path 정의 — 컴포넌트 밖 모듈 상수(렌더마다 재생성하지 않는다).
// CS_ICON_NAMES가 여기서 파생되므로 아이콘을 추가하면 "고르는 UI"에도 자동 반영된다.
const CS_ICON_PATHS = {
  grip: <><path d="M5 6h14M5 12h14M5 18h14"/></>,
  home: <><path d="M3 11L12 4l9 7v9a1 1 0 01-1 1h-5v-7H9v7H4a1 1 0 01-1-1v-9z"/></>,
  chart: <><path d="M4 20h16M7 16V9M12 16V5M17 16v-4"/></>,
  coin: <><circle cx="12" cy="12" r="8"/><path d="M9 9h5a2 2 0 010 4H10m0 0v3m4-3h-4"/></>,
  game: <><rect x="3" y="7" width="18" height="11" rx="3"/><path d="M8 12h3m-1.5-1.5v3M15 11.5h.01M17 13.5h.01"/></>,
  chat: <><path d="M4 6a3 3 0 013-3h10a3 3 0 013 3v7a3 3 0 01-3 3H10l-4 4v-4H7a3 3 0 01-3-3V6z"/></>,
  user: <><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 4-7 8-7s8 3 8 7"/></>,
  plus: <><path d="M12 5v14M5 12h14"/></>,
  search: <><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></>,
  bell: <><path d="M6 9a6 6 0 0112 0v4l2 3H4l2-3V9z"/><path d="M10 19a2 2 0 004 0"/></>,
  arrow: <><path d="M5 12h14M13 6l6 6-6 6"/></>,
  back: <><path d="M19 12H5M11 6l-6 6 6 6"/></>,
  more: <><circle cx="5" cy="12" r="1.2"/><circle cx="12" cy="12" r="1.2"/><circle cx="19" cy="12" r="1.2"/></>,
  bookmark: <><path d="M6 4h12v17l-6-4-6 4V4z"/></>,
  heart: <><path d="M12 20l-7-7a4.5 4.5 0 016.4-6.4L12 7l.6-.4A4.5 4.5 0 0119 13l-7 7z"/></>,
  share: <><circle cx="6" cy="12" r="2"/><circle cx="18" cy="6" r="2"/><circle cx="18" cy="18" r="2"/><path d="M8 11l8-4m-8 6l8 4"/></>,
  filter: <><path d="M4 5h16M7 12h10M10 19h4"/></>,
  sparkle: <><path d="M12 4v6m0 4v6M4 12h6m4 0h6M7 7l3 3m4 4l3 3M7 17l3-3m4-4l3-3"/></>,
  eye: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></>,
  eyeOff: <><path d="M3 3l18 18M10 5.5A11 11 0 0112 5c6.5 0 10 7 10 7a18 18 0 01-3.2 4M6 6.5C3.2 8.4 2 12 2 12s3.5 7 10 7c1.6 0 3-0.3 4.3-0.8M9.9 9.9a3 3 0 104.2 4.2"/></>,
  bolt: <><path d="M13 3L4 14h6l-1 7 9-11h-6l1-7z"/></>,
  info: <><circle cx="12" cy="12" r="9"/><path d="M12 8h.01M11 12h1v5h1"/></>,
  check: <><path d="M5 12l4 4 10-10"/></>,
  star: <><path d="M12 3l2.5 6 6.5.5-5 4.5 1.5 6.5L12 17l-5.5 3.5L8 14l-5-4.5L9.5 9z"/></>,
  book: <><path d="M4 4h7a3 3 0 013 3v13a2 2 0 00-2-2H4V4zM20 4h-7a3 3 0 00-3 3v13a2 2 0 012-2h8V4z"/></>,
  wallet: <><path d="M3 7a2 2 0 012-2h13a2 2 0 012 2v2H5a2 2 0 00-2 2V7z"/><path d="M3 11a2 2 0 012-2h15v8a2 2 0 01-2 2H5a2 2 0 01-2-2v-6zM16 14h2"/></>,
  target: <><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.5"/></>,
  flame: <><path d="M12 3s5 5 5 10a5 5 0 01-10 0c0-2 1-3 2-4-.5 2 .5 3 1.5 3 .5 0 1-1 1-2 0-3-.5-5 .5-7z"/></>,
  // ── 이모지→라인아이콘 전환 신규 세트 (2026-07-08 design doc §4) ──
  lock: <><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 018 0v3"/></>,
  unlock: <><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 017.5-2"/></>,
  settings: <><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M19.1 4.9L17 7M7 17l-2.1 2.1"/></>,
  link: <><path d="M10 7l1-1a4 4 0 015.5 5.5l-1 1M14 17l-1 1a4 4 0 01-5.5-5.5l1-1M9 15l6-6"/></>,
  download: <><path d="M12 3v12M7 11l5 5 5-5M5 21h14"/></>,
  upload: <><path d="M12 21V9M7 13l5-5 5 5M5 3h14"/></>,
  trophy: <><path d="M8 4h8v5a4 4 0 01-8 0V4zM8 6H5v1a3 3 0 003 3M16 6h3v1a3 3 0 01-3 3M9 20h6M12 15v5"/></>,
  medal: <><circle cx="12" cy="14" r="6"/><path d="M8.5 2.5L12 8.5M15.5 2.5L12 8.5"/><path d="M12 11l1 2 2.2.3-1.6 1.5.4 2.2-2-1-2 1 .4-2.2L8.8 13.3l2.2-.3z"/></>,
  crown: <><path d="M4 8l3.5 8h9L20 8l-5 4-3-6-3 6-5-4zM7.5 20h9"/></>,
  trash: <><path d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2M6 7l1 13a1 1 0 001 1h8a1 1 0 001-1l1-13M10 11v6M14 11v6"/></>,
  edit: <><path d="M4 20h4l10-10-4-4L4 16v4zM14 6l4 4"/></>,
  clipboard: <><rect x="5" y="4" width="14" height="17" rx="2"/><path d="M9 4a1 1 0 011-1h4a1 1 0 011 1v2h-6V4zM9 12h6M9 16h6"/></>,
  note: <><path d="M5 3h14a1 1 0 011 1v12l-4 4H5a1 1 0 01-1-1V4a1 1 0 011-1zM8 9h8M8 13h5M16 20v-4h4"/></>,
  file: <><path d="M6 3h8l4 4v13a1 1 0 01-1 1H6a1 1 0 01-1-1V4a1 1 0 011-1zM14 3v4h4"/></>,
  refresh: <><path d="M20 12a8 8 0 11-2.3-5.6M18 3v4h-4"/></>,
  // ⚠ 2026-09-10 역동기화: 9/8 스튜디오 헤더 되돌리기(StudioScreen.tsx)용으로 파생본 cs.jsx에만
  //   추가돼 있었다. cs:sync가 이 키를 조용히 지웠다 — 심볼 가드는 최상위 선언만 보고 객체 키는 못 본다.
  undo: <><path d="M9 14L4 9l5-5"/><path d="M4 9h10.5a5.5 5.5 0 010 11H11"/></>,
  calendar: <><rect x="4" y="5" width="16" height="16" rx="2"/><path d="M4 9h16M8 3v4M16 3v4"/></>,
  clock: <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>,
  bank: <><path d="M3 9l9-5 9 5M4 9h16M5 9v9M9 9v9M15 9v9M19 9v9M3 19h18"/></>,
  building: <><rect x="5" y="3" width="14" height="18" rx="1"/><path d="M9 7h2M13 7h2M9 11h2M13 11h2M10 21v-3h4v3"/></>,
  shield: <><path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3z"/></>,
  briefcase: <><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2M3 13h18"/></>,
  bulb: <><path d="M9 18h6M10 21h4M12 3a6 6 0 00-4 10.5c.6.6 1 1.3 1 2h6c0-.7.4-1.4 1-2A6 6 0 0012 3z"/></>,
  signal: <><path d="M12 20V10M8 20v-4M16 20v-7M5 8a10 10 0 0114 0M8 11a5.5 5.5 0 018 0"/></>,
  news: <><path d="M4 5h13a1 1 0 011 1v13a2 2 0 002 2H5a1 1 0 01-1-1V5zM8 9h7M8 13h7M8 17h4M18 9v10a2 2 0 002 2 1 1 0 001-1V9"/></>,
  compass: <><circle cx="12" cy="12" r="9"/><path d="M15.5 8.5l-2 5-5 2 2-5z"/></>,
  gift: <><rect x="4" y="9" width="16" height="12" rx="1"/><path d="M4 13h16M12 9v12M12 9C10 5 6 6 8 8s4 1 4 1zM12 9c2-4 6-3 4-1s-4 1-4 1z"/></>,
  warning: <><path d="M12 4l9 16H3l9-16zM12 10v4M12 17v.5"/></>,
  close: <><path d="M6 6l12 12M18 6L6 18"/></>,
  // 오른쪽 꺾쇠 1종만 둔다. 아래·위는 호출부 래퍼의 rotate(90/-90deg)로 파생한다.
  // (2026-09-10 내 매매 시트 2b — 유니코드 '›'·'∨' 글자 대신. 글자는 iOS 폰트마다 두께·baseline이 흔들린다)
  chevron: <><path d="M9 6l6 6-6 6"/></>,
  flag: <><path d="M5 21V4M5 4h12l-2 4 2 4H5"/></>,
  ticket: <><path d="M4 8a2 2 0 012-2h12a2 2 0 012 2 2 2 0 000 4 2 2 0 010 4H6a2 2 0 01-2-2 2 2 0 000-4V8zM14 6v12"/></>,
  scale: <><path d="M12 3v18M7 21h10M6 7l-3 6a3 3 0 006 0l-3-6zM18 7l-3 6a3 3 0 006 0l-3-6zM4 7h16M8 7l4-2 4 2"/></>,
  moon: <><path d="M20 14a8 8 0 01-9.5-11 8 8 0 109.5 11z"/></>,
  candle: <><path d="M8 4v4M8 16v4M6 8h4v8H6zM16 6v3M16 15v3M14 9h4v6h-4z"/></>,
  dna: <><path d="M7 3c0 5 10 6 10 9s-10 4-10 9M17 3c0 5-10 6-10 9s10 4 10 9M8.5 6h7M9.5 9h5M9.5 15h5M8.5 18h7"/></>,
  brain: <><path d="M8.5 5A3 3 0 006 8a2.5 2.5 0 00-.5 4.8A2.5 2.5 0 008 17a2.5 2.5 0 004 .3 2.5 2.5 0 004-.3 2.5 2.5 0 002.5-4.2A2.5 2.5 0 0018 8a3 3 0 00-3-3 2.6 2.6 0 00-3.5-.6A2.6 2.6 0 008.5 5z"/><path d="M12 4.4v13.2"/></>,
  gem: <><path d="M5 8l3-4h8l3 4-7 12L5 8zM5 8h14M9 4l3 4 3-4M9 8l3 12 3-12"/></>,
  chartDown: <><path d="M4 6l6 6 4-4 6 6M20 14v-4h-4"/></>,
  thumbUp: <><path d="M7 22V11l4-7a2 2 0 013 1.7V9h4a2 2 0 011.9 2.6l-1.6 7A2 2 0 0120.3 22H7zM7 11H4v11h3"/></>,
  globe: <><circle cx="12" cy="12" r="9"/><path d="M3 12h18"/><path d="M12 3a14 14 0 010 18M12 3a14 14 0 000 18"/></>,
  // 황소 얼굴(뿔 2 + 둥근 얼굴 + 눈 2점 + 입선) — /collection 시안 IcoBull과 같은 path.
  // MY '황소 컬렉션' 카드가 이 이름을 불렀는데 세트에 없어 폴백 빈 원이 떴다(2026-09-11 추가).
  // 눈만 fill="currentColor"로 채운다(CSIcon 기본 fill="none"이라 명시해야 점이 보인다).
  bull: <><path d="M4 5c2 0 3.1 1.4 3.5 3"/><path d="M20 5c-2 0-3.1 1.4-3.5 3"/><path d="M7.2 8C6 9 5.3 10.6 5.3 12.3 5.3 16 8.1 19 12 19s6.7-3 6.7-6.7c0-1.7-.7-3.3-1.9-4.3"/><circle cx="9.6" cy="12" r="0.7" fill="currentColor"/><circle cx="14.4" cy="12" r="0.7" fill="currentColor"/><path d="M10 15.4h4"/></>,
};

/** 사용 가능한 아이콘 이름 전체. 아이콘 선택기(목표 화면 등)의 단일 출처. */
const CS_ICON_NAMES = Object.freeze(Object.keys(CS_ICON_PATHS));

function CSIcon({ name, size = 18, stroke = 1.6, color = "currentColor" }) {
  const c = color, sw = stroke;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      {CS_ICON_PATHS[name] || <circle cx="12" cy="12" r="9" />}
    </svg>
  );
}

/* ─────────── List Row ─────────── */
function CSRow({ left, title, sub, right, onClick, divider = true }) {
  return (
    <div onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(e); } } : undefined}
      style={{
        display: "flex", alignItems: "center", gap: 12, padding: "12px 0",
        borderBottom: divider ? "1px solid var(--cs-line)" : "none",
        cursor: onClick ? "pointer" : "default",
      }}>
      {left && <div style={{ flexShrink: 0 }}>{left}</div>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
        {sub && <div style={{ fontSize: 12, color: "var(--cs-mute)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</div>}
      </div>
      {right && <div style={{ flexShrink: 0, textAlign: "right" }}>{right}</div>}
    </div>
  );
}

/* ─────────── Sparkline (svg) ─────────── */
function CSSpark({ data, w = 56, h = 18, color, fill }) {
  if (!data?.length) return null;
  const min = Math.min(...data), max = Math.max(...data);
  const r = max - min || 1;
  const pts = data.map((v, i) => [i * (w / (data.length - 1)), h - ((v - min) / r) * (h - 2) - 1]);
  const d = pts.map((p, i) => (i === 0 ? "M" : "L") + p[0].toFixed(1) + "," + p[1].toFixed(1)).join(" ");
  const positive = data[data.length - 1] >= data[0];
  const c = color || (positive ? "var(--cs-rise)" : "var(--cs-fall)");
  return (
    <svg width={w} height={h} style={{ overflow: "visible" }}>
      {fill && <path d={d + ` L ${w},${h} L 0,${h} Z`} fill={c} opacity={0.12} />}
      <path d={d} fill="none" stroke={c} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ─────────── Area Chart (svg) ─────────── */
/* 축 눈금 라벨 — 큰 값은 억/만으로 축약한다.
   ⚠ 왜: 라벨에 주어진 가로 폭은 padL(40) - 6 = 34px뿐인데(textAnchor="end"라 왼쪽으로 자란다),
   BULL 잔액처럼 9자리가 들어오면 앞자리가 viewBox 밖으로 잘려 "681,007,424"가 "8,029"로 보였고,
   세 눈금이 위부터 4,337 / 1,183 / 8,029로 읽혀 **축이 뒤집힌 것처럼** 보였다(2026-08-14 SE3 육안).
   눈금 세 개가 서로 구분돼야 하므로, 값의 범위(r)가 좁을수록 소수 자릿수를 늘린다.
   예) 6.8억대에서 범위가 630만이면 6.843억 / 6.811억 / 6.780억. */
function axisTick(v, r) {
  const abs = Math.abs(v);
  if (abs >= 1e8) return (v / 1e8).toFixed(r < 1e7 ? 3 : r < 1e8 ? 2 : 1) + "억";
  if (abs >= 1e4) return (v / 1e4).toFixed(r < 1e3 ? 2 : r < 1e4 ? 1 : 0) + "만";
  return Math.round(v).toLocaleString();
}

function CSAreaChart({ data, w = 320, h = 160, color, fill = true, dots, baseline, labels }) {
  if (!data?.length) return null;
  const min = Math.min(...data), max = Math.max(...data);
  const r = max - min || 1;
  const padL = 40, padR = 8, padT = 6, padB = 18; // padL: 축약 라벨(최대 6자)이 들어갈 폭
  const cw = w - padL - padR, ch = h - padT - padB;
  const pts = data.map((v, i) => [padL + i * (cw / (data.length - 1)), padT + ch - ((v - min) / r) * ch]);
  const d = pts.map((p, i) => (i === 0 ? "M" : "L") + p[0].toFixed(1) + "," + p[1].toFixed(1)).join(" ");
  const positive = data[data.length - 1] >= data[0];
  const c = color || (positive ? "var(--cs-rise)" : "var(--cs-fall)");
  const yTicks = [max, min + r * 0.5, min];
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: "block" }}>
      {yTicks.map((y, i) => {
        const yp = padT + ch * (i / 2);
        return (
          <g key={i}>
            <line x1={padL} x2={w - padR} y1={yp} y2={yp} stroke="var(--cs-line)" strokeWidth={0.5} strokeDasharray={i === 1 ? "2,3" : "0"} />
            <text x={padL - 6} y={yp + 3} fontSize="9" fill="var(--cs-mute)" fontFamily="var(--cs-font-mono)" textAnchor="end">{axisTick(y, r)}</text>
          </g>
        );
      })}
      {fill && <path d={d + ` L ${pts[pts.length - 1][0]},${padT + ch} L ${pts[0][0]},${padT + ch} Z`} fill={c} opacity={0.12} />}
      <path d={d} fill="none" stroke={c} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
      {dots && pts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r={2} fill="var(--cs-surface)" stroke={c} strokeWidth={1.5} />)}
      {labels && labels.map((l, i) => {
        const xp = padL + i * (cw / (labels.length - 1));
        return <text key={i} x={xp} y={h - 4} fontSize="9" fill="var(--cs-mute)" fontFamily="var(--cs-font-mono)" textAnchor="middle">{l}</text>;
      })}
    </svg>
  );
}

/* ─────────── Candle Chart ─────────── */
function CSCandles({ data, w = 320, h = 160 }) {
  if (!data?.length) return null;
  const all = data.flatMap(d => [d.h, d.l]);
  const min = Math.min(...all), max = Math.max(...all);
  const r = max - min || 1;
  const padL = 40, padR = 8, padT = 6, padB = 6; // padL: 축약 라벨(최대 6자)이 들어갈 폭
  const cw = w - padL - padR, ch = h - padT - padB;
  const slot = cw / data.length;
  const cwidth = Math.min(slot * 0.55, 8);
  const yp = v => padT + ch - ((v - min) / r) * ch;
  const yTicks = [max, min + r * 0.5, min];
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: "block" }}>
      {yTicks.map((y, i) => {
        const ypos = padT + ch * (i / 2);
        return (
          <g key={i}>
            <line x1={padL} x2={w - padR} y1={ypos} y2={ypos} stroke="var(--cs-line)" strokeWidth={0.5} />
            <text x={padL - 6} y={ypos + 3} fontSize="9" fill="var(--cs-mute)" fontFamily="var(--cs-font-mono)" textAnchor="end">{axisTick(y, r)}</text>
          </g>
        );
      })}
      {data.map((d, i) => {
        const up = d.c >= d.o;
        const x = padL + slot * i + slot / 2;
        const color = up ? "var(--cs-rise)" : "var(--cs-fall)";
        return (
          <g key={i}>
            <line x1={x} x2={x} y1={yp(d.h)} y2={yp(d.l)} stroke={color} strokeWidth={1} />
            <rect x={x - cwidth/2} y={yp(Math.max(d.o, d.c))} width={cwidth} height={Math.max(1, Math.abs(yp(d.o) - yp(d.c)))} fill={color} />
          </g>
        );
      })}
    </svg>
  );
}

/* ─────────── Donut Chart ─────────── */
function CSDonut({ data, size = 140, thickness = 18, center }) {
  const total = data.reduce((a, b) => a + b.value, 0) || 1;
  const r = size / 2 - thickness / 2;
  const c = 2 * Math.PI * r;
  let off = 0;
  return (
    <div style={{ position: "relative", width: size, height: size, display: "inline-block" }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size/2} cy={size/2} r={r} stroke="var(--cs-surface-2)" strokeWidth={thickness} fill="none" />
        {data.map((d, i) => {
          const dash = (d.value / total) * c;
          const seg = <circle key={i} cx={size/2} cy={size/2} r={r} stroke={d.color} strokeWidth={thickness} fill="none"
            strokeDasharray={`${dash} ${c - dash}`} strokeDashoffset={-off} strokeLinecap="butt" />;
          off += dash;
          return seg;
        })}
      </svg>
      {center && (
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
          {center}
        </div>
      )}
    </div>
  );
}

/* ─────────── VoteBar (2분할 비율 투표 막대) ───────────
   for/against 두 선택지를 득표 비율(width %)로 나눈 막대.
   mySide로 내 선택을 강조. 정적 버튼(CSBtn)·세그먼트(CSTabs)로
   표현 불가한 동적 비율 패턴 전용 표준 부품. */
function CSVoteBar({ dist, mySide, labels, onVote, voting, unit = "명", style }) {
  const total = (dist?.for || 0) + (dist?.against || 0);
  const forPct = total > 0 ? Math.round((dist.for / total) * 100) : 50;
  return (
    <div style={style}>
      <div style={{ display: "flex", height: 44, borderRadius: "var(--cs-r-md)", overflow: "hidden", border: "1px solid var(--cs-line)" }}>
        <button onClick={() => onVote("for")} disabled={voting}
          style={{ width: `${forPct}%`, minWidth: 64, border: "none", cursor: voting ? "default" : "pointer",
            background: mySide === "for" ? "var(--cs-accent)" : "var(--cs-surface-2)",
            color: mySide === "for" ? "var(--cs-accent-ink)" : "var(--cs-ink)", fontWeight: 800, fontSize: 13, transition: "background var(--cs-dur-fast) ease, color var(--cs-dur-fast) ease" }}>
          ✓ {labels.for} {total > 0 ? `${forPct}%` : ""}
        </button>
        <button onClick={() => onVote("against")} disabled={voting}
          style={{ width: `${100 - forPct}%`, minWidth: 64, border: "none", cursor: voting ? "default" : "pointer",
            background: mySide === "against" ? "var(--cs-mute)" : "var(--cs-surface-2)",
            color: mySide === "against" ? "var(--cs-accent-ink)" : "var(--cs-ink)", fontWeight: 800, fontSize: 13, transition: "background var(--cs-dur-fast) ease, color var(--cs-dur-fast) ease" }}>
          ✗ {labels.against} {total > 0 ? `${100 - forPct}%` : ""}
        </button>
      </div>
      <div style={{ fontSize: 11.5, color: "var(--cs-mute)", textAlign: "center", marginTop: 6 }}>
        총 {total}{unit} 참여{mySide && <span style={{ color: "var(--cs-accent)", fontWeight: 700 }}> · 내 선택: {labels[mySide]}</span>}
      </div>
    </div>
  );
}

/* keyframes(csFade/csPop/csSlideUp/csPulse)는 cs-tokens.css 로 이전됨 */

/* ─────────────────────────────────────────────
   BullCoin — BULL 화폐 인라인 아이콘 (T3 배칭E 채택 2026-07-10)
   🐂 이모지 화폐 표기 대체. 텍스트 옆 인라인 정렬(vertical-align) 전제.
   자산: brand/bull-coin-b@128.png (미니멀 각인 — 소형 가독용 채택안)
   ⚠ 시안 전면 교체 예정(효신님) — 교체 시 이 파일 경로만 갈면 전 화면 일괄 반영. */
function BullCoin({ size = 14, style }) {
  return (
    <img
      src="/redesign-assets/brand/bull-coin-b@128.png"
      alt="BULL"
      width={size}
      height={size}
      style={{ display: "inline-block", verticalAlign: "-0.12em", objectFit: "contain", ...style }}
    />
  );
}

export {
  CSCard, CSBtn, CSInput, CSTabs, CSBottomNav, CSAppBar, CSModal,
  CSChartContainer, CSMetric, CSAvatar, CSBadge, CSTag, CSIconBtn,
  CSIcon, CSRow, CSSpark, CSAreaChart, CSCandles, CSDonut, CSVoteBar,
  BullCoin,
  CS_ICON_NAMES,
};
